import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { z } from 'zod';

import { useBusiness } from 'src/features/auth/useAuth';
import { METHOD_LABELS, METHOD_OPTIONS } from 'src/features/payments/paymentUtils';
import type { PaymentMethod } from 'src/features/payments/types';
import { fieldErrorsFromApiError } from 'src/shared/api/errors';
import { ApiError } from 'src/shared/api/types';
import AppModal from 'src/shared/components/ui/AppModal';
import { todayKey } from 'src/shared/utils/datetime';
import { MONEY, currencySymbol, decimalToNumber, toAmountInput } from 'src/shared/utils/money';
import { notify } from 'src/shared/utils/notify';
import rhfInput from 'src/shared/utils/rhfInput';
import zodResolver from 'src/shared/utils/zodResolver';
import { categoryChoices } from './financeUtils';
import type { Expense, ExpenseCategory, ExpensePayload } from './types';
import { useCreateExpense, useUpdateExpense } from './useFinance';

const schema = z.object({
  amount: z.string().regex(MONEY, 'Indica un monto con hasta dos decimales'),
  categoryId: z.string().min(1, 'Elige una categoría'),
  method: z.string().min(1, 'Elige cómo se pagó'),
  spentOn: z.string().min(1, 'Indica la fecha del gasto'),
  description: z.string().min(1, 'Describe el gasto'),
  supplier: z.string(),
  documentNumber: z.string(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  amount: '',
  categoryId: '',
  method: 'cash',
  spentOn: '',
  description: '',
  supplier: '',
  documentNumber: '',
};

const fromExpense = (expense: Expense): FormValues => ({
  amount: toAmountInput(expense.amount),
  categoryId: String(expense.categoryId),
  method: expense.method,
  spentOn: expense.spentOn,
  description: expense.description,
  supplier: expense.supplier ?? '',
  documentNumber: expense.documentNumber ?? '',
});

const toPayload = (values: FormValues): ExpensePayload => ({
  amount: decimalToNumber(values.amount),
  categoryId: Number(values.categoryId),
  method: values.method as PaymentMethod,
  spentOn: values.spentOn,
  description: values.description.trim(),
  supplier: values.supplier.trim() || null,
  documentNumber: values.documentNumber.trim() || null,
});

const errorMessage = (error: Error | null): string | null => {
  if (!error) return null;
  if (!(error instanceof ApiError)) {
    return 'No pudimos conectar con el servidor. Revisa tu conexión.';
  }
  if (error.status === 422 && error.errors[0]?.field === 'categoryId') {
    return 'Esa categoría ya no está disponible. Elige otra.';
  }
  if (error.status === 422) return error.errors[0]?.message ?? 'Revisa los datos del formulario.';
  if (error.status === 403) return 'No tienes permiso para registrar gastos.';
  return 'No pudimos guardar el gasto. Inténtalo de nuevo.';
};

interface ExpenseFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  /** Absent when adding; present when editing. */
  expense?: Expense;
  /** Every category, archived ones included; the select decides what to offer. */
  categories: ExpenseCategory[];
}

export default function ExpenseFormModal({
  isOpen,
  toggle,
  expense,
  categories,
}: ExpenseFormModalProps) {
  const { data: business } = useBusiness();
  const create = useCreateExpense();
  const update = useUpdateExpense();
  const isEditing = Boolean(expense);
  const mutation = isEditing ? update : create;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const choices = categoryChoices(categories, expense?.categoryId);
  const firstChoice = choices[0]?.id;

  useEffect(() => {
    if (isOpen) {
      reset(
        expense
          ? fromExpense(expense)
          : {
              ...EMPTY,
              categoryId: firstChoice ? String(firstChoice) : '',
              // The business's day, not the browser's: at 20:00 in Guayaquil a
              // UTC clock is already on tomorrow.
              spentOn: todayKey(business?.timezone),
            }
      );
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, expense, reset]);

  const onSubmit = (values: FormValues) => {
    const payload = toPayload(values);
    const onSuccess = () => {
      notify.success(isEditing ? 'Gasto actualizado' : 'Gasto registrado');
      toggle();
    };
    const onError = (error: Error) => {
      for (const [field, message] of Object.entries(fieldErrorsFromApiError(error))) {
        if (field in EMPTY) setError(field as keyof FormValues, { message });
      }
    };
    if (expense) {
      update.mutate({ id: expense.id, data: payload }, { onSuccess, onError });
    } else {
      create.mutate(payload, { onSuccess, onError });
    }
  };

  const message = errorMessage(mutation.error);

  return (
    <AppModal
      isOpen={isOpen}
      toggle={toggle}
      backdrop="static"
      title={isEditing ? 'Editar gasto' : 'Registrar gasto'}
      onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      footer={
        <>
          <Button color="link" onClick={toggle} type="button">
            Cancelar
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={mutation.isPending || choices.length === 0}
          >
            {mutation.isPending && <Spinner size="sm" className="me-2" />}
            {isEditing ? 'Guardar' : 'Registrar gasto'}
          </Button>
        </>
      }
    >
      {choices.length === 0 && (
        <Alert color="warning">
          No hay categorías activas. Crea una en la pestaña «Configuración» antes de registrar
          gastos.
        </Alert>
      )}

      <fieldset className="v-form-section">
        <legend className="v-label">Qué y cuánto</legend>
        <FormGroup>
          <Label for="expenseDescription">Concepto *</Label>
          <Input
            id="expenseDescription"
            autoComplete="off"
            placeholder="Esmaltes y limas"
            invalid={Boolean(errors.description)}
            {...rhfInput(register('description'))}
          />
          {errors.description && <FormFeedback>{errors.description.message}</FormFeedback>}
        </FormGroup>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="expenseAmount">Monto *</Label>
              <InputGroup>
                <InputGroupText>{currencySymbol(business?.currency)}</InputGroupText>
                <Input
                  id="expenseAmount"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="15.50"
                  invalid={Boolean(errors.amount)}
                  {...rhfInput(register('amount'))}
                />
                {errors.amount && <FormFeedback>{errors.amount.message}</FormFeedback>}
              </InputGroup>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="expenseCategory">Categoría *</Label>
              <Input
                id="expenseCategory"
                type="select"
                invalid={Boolean(errors.categoryId)}
                {...rhfInput(register('categoryId'))}
              >
                {choices.map((choice) => (
                  <option key={choice.id} value={choice.id}>
                    {choice.label}
                  </option>
                ))}
              </Input>
              {errors.categoryId && <FormFeedback>{errors.categoryId.message}</FormFeedback>}
            </FormGroup>
          </Col>
        </Row>
      </fieldset>

      <fieldset className="v-form-section">
        <legend className="v-label">Detalles</legend>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="expenseDate">Fecha *</Label>
              <Input
                id="expenseDate"
                type="date"
                invalid={Boolean(errors.spentOn)}
                {...rhfInput(register('spentOn'))}
              />
              {errors.spentOn && <FormFeedback>{errors.spentOn.message}</FormFeedback>}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="expenseMethod">Forma de pago *</Label>
              <Input id="expenseMethod" type="select" {...rhfInput(register('method'))}>
                {METHOD_OPTIONS.map((method) => (
                  <option key={method} value={method}>
                    {METHOD_LABELS[method]}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="expenseSupplier">Proveedor</Label>
              <Input id="expenseSupplier" autoComplete="off" {...rhfInput(register('supplier'))} />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup className="mb-md-0">
              <Label for="expenseDocument">Nº de documento</Label>
              <Input
                id="expenseDocument"
                autoComplete="off"
                {...rhfInput(register('documentNumber'))}
              />
            </FormGroup>
          </Col>
        </Row>
      </fieldset>

      {/* Under the fields, right above "Registrar gasto": a refusal is read
          where the thumb already is, not at the top of a scrolled form. */}
      {message && (
        <Alert color="danger" role="alert" className="mb-0">
          {message}
        </Alert>
      )}
    </AppModal>
  );
}
